import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import rushLogo from './rush-logo.png';
import './rush.css';
import './auth.css';
import { loginSuccess } from '../../slices/authSlice';
import { storeAuthToken } from '../../utils/authToken';

// Magic-link landing page. The token in the URL is consumed once; the server
// either signs us in directly or hands back a challenge token for 2FA.
const RushJoinPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [status, setStatus] = useState('loading'); // loading | name | error
    const [displayName, setDisplayName] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const consume = async (name) => {
        setSubmitting(true);
        try {
            const res = await axios.post('/api/magic-link/consume', {
                token,
                displayName: name,
            });

            if (res.data.needs2FA === 'challenge') {
                navigate(`/two-factor/challenge?ct=${encodeURIComponent(res.data.challengeToken)}&r=1`, { replace: true });
                return;
            }

            const authToken = res.data.token;
            storeAuthToken(authToken, true);
            const me = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            dispatch(loginSuccess({ token: authToken, user: me.data }));

            // Guests don't have a dashboard — route them straight to their
            // scoped project. The 2FA grace prompt still trumps everything.
            const isGuest = res.data?.user?.accountType === 'guest';
            const guestDest = isGuest && res.data.guestProjectId
                ? `/guest/project/${res.data.guestProjectId}`
                : null;
            navigate(
                res.data.mustEnroll2FA
                    ? '/two-factor/setup'
                    : (guestDest || '/dashboard'),
                { replace: true },
            );
        } catch (err) {
            setError(err.response?.data?.msg || 'This link is invalid or has expired.');
            setStatus('error');
            setSubmitting(false);
        }
    };

    useEffect(() => {
        // First-pass: try to consume without a display name. Server creates
        // the account on first use of invite tokens, but the name is optional
        // — if you want to capture it, render the form before consuming.
        // We prompt the user before consuming so we can capture a name once.
        setStatus('name');
    }, []);

    if (status === 'loading') {
        return (
            <div className="rs auth-page">
                <section className="auth-form-panel">
                    <div className="auth-form-body">Loading…</div>
                </section>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="rs auth-page">
                <section className="auth-form-panel">
                    <header className="auth-form-header">
                        <Link to="/" className="rs-logo rs-logo-img">
                            <img src={rushLogo} alt="Rush Studio" />
                        </Link>
                    </header>
                    <div className="auth-form-body">
                        <h1 className="auth-title">Link unavailable</h1>
                        <p className="auth-sub">{error}</p>
                        <p className="auth-sub">
                            Ask your workspace admin for a fresh invite, or{' '}
                            <Link to="/login">sign in with a password</Link>.
                        </p>
                    </div>
                </section>
            </div>
        );
    }

    // status === 'name'
    return (
        <div className="rs auth-page">
            <section className="auth-form-panel">
                <header className="auth-form-header">
                    <Link to="/" className="rs-logo rs-logo-img">
                        <img src={rushLogo} alt="Rush Studio" />
                    </Link>
                </header>
                <div className="auth-form-body">
                    <div className="auth-eyebrow">Join your workspace</div>
                    <h1 className="auth-title">What should we call you?</h1>
                    <p className="auth-sub">
                        Your invite is good — just tell us your name and we'll drop you in.
                        No password to remember; future sign-ins go through this same email.
                    </p>
                    <form
                        className="auth-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            consume(displayName.trim());
                        }}
                    >
                        <label className="field">
                            <span>Your name</span>
                            <input
                                type="text"
                                className="input input-lg"
                                placeholder="Alex Rivera"
                                required
                                autoFocus
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                            />
                        </label>
                        <button
                            className="btn btn-accent auth-submit"
                            type="submit"
                            disabled={submitting || !displayName.trim()}
                        >
                            {submitting ? 'Signing you in…' : 'Continue'}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default RushJoinPage;
